import * as Sentry from '@sentry/react';
import Immutable from 'immutable';

import {
  getGradebookAPIUtil,
  releaseGradebookAPIUtil,
  revertDraftGradebookAPIUtil,
  updateGradebookAPIUtil,
} from 'bundles/author-gradebook/utils/apiUtils';
import { formatSingleUserToHOTFormat, toBEFormat, toHOTFormat } from 'bundles/author-gradebook/utils/transformer';

export type GradebookActionStatus = {
  loading?: boolean;
  error?: boolean;
};

type LoadGradebookActionPayload = {
  sessionId: string;
  groupId: string;
  partnerId?: string;
  /**
   * when passed, the endpoint will filter by itemId
   * https://coursera.atlassian.net/browse/GRADE-4870
   */
  itemId?: string;
  done?: () => void;
  passingThreshold?: number;
  setStatus?: (status: GradebookActionStatus) => void;
};

/**
 * Fluxible action to load Gradebook into Gradebook store
 * @param actionContext $TSFixMe
 * @param LoadGradebookActionPayload
 * @returns undefined
 */
export const loadGradebook = (
  actionContext: $TSFixMe,
  { sessionId, groupId, partnerId, done, passingThreshold, itemId, setStatus }: LoadGradebookActionPayload
) => {
  if (!groupId) {
    return;
  }

  actionContext.dispatch('LOAD_GRADEBOOK_START', { sessionId, groupId, passingThreshold });

  // if fetching status tracking is requested, apply updates here
  setStatus?.({ loading: true, error: false });

  getGradebookAPIUtil(groupId, partnerId, itemId)
    .then((data) => {
      const { userGradebooks, itemNames, draftCreatorProfileNames, gradedAssignmentGroups, courseId } = data;
      const formattedData = toHOTFormat(userGradebooks, itemNames, gradedAssignmentGroups);

      actionContext.dispatch('LOAD_GRADEBOOK_SUCCESS', {
        sessionId,
        courseId,
        data: formattedData,
        itemNames,
        gradedAssignmentGroups,
        draftCreatorProfileNames,
      });
    })
    .then(() => {
      setStatus?.({ loading: false, error: false });

      if (done) {
        return done();
      }
      return Promise.resolve();
    })
    .catch((error) => {
      setStatus?.({ loading: false, error: true });

      actionContext.dispatch('LOAD_GRADEBOOK_FAIL', { sessionId });

      Sentry.captureException(error, {
        extra: { type: 'Gradebook Load Failure' },
      });

      // eslint-disable-next-line no-console
      console.log(error);
    });
};

export const saveGradebook = (actionContext: $TSFixMe, { sessionId, groupId, data }: $TSFixMe) => {
  const formattedData = toBEFormat(data);

  updateGradebookAPIUtil(groupId, formattedData)
    .then((result) => {
      const { userGradebooks } = result;
      const formattedUserGradebooks = {};
      Object.keys(userGradebooks).forEach((userId) => {
        // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
        formattedUserGradebooks[userId] = formatSingleUserToHOTFormat(userGradebooks[userId]);
      });
      actionContext.dispatch('SAVE_GRADEBOOK_SUCCESS', { sessionId, ...result, formattedUserGradebooks });
    })
    .catch((error) => {
      actionContext.dispatch('SAVE_GRADEBOOK_FAIL', { sessionId, message: error.responseJSON.message });

      Sentry.captureException(error, {
        extra: { type: 'Gradebook Save Failure' },
      });
    });

  actionContext.dispatch('SAVE_GRADEBOOK_START', { sessionId });
};

export const checkConflicts = (actionContext: $TSFixMe, { sessionId, groupId, gradebook, version }: $TSFixMe) => {
  actionContext.dispatch('CONFLICT_CHECK_START', { sessionId });

  // get latest gradebook
  getGradebookAPIUtil(groupId).then((data) => {
    const formattedData = toHOTFormat(data.userGradebooks, data.itemNames, data.gradedAssignmentGroups);

    let hasAnyGradeChanged = false;
    // compare existing itemGrades with new itemGrades, and show prompt if grades have changed
    gradebook.forEach((learner: $TSFixMe, userId: $TSFixMe) => {
      // compare existing and new itemGrades for each learner, as they might have made progress
      const existingGrades = learner.get('itemGrades');
      const newGrades = formattedData.getIn([userId, 'itemGrades']);

      if (!Immutable.is(existingGrades, newGrades)) {
        hasAnyGradeChanged = true;
        return false;
      }

      return true;
    });

    if (hasAnyGradeChanged) {
      // show user prompt to reload page and review, or continue to release
      actionContext.dispatch('CONFLICT_CHECK_GRADE_CHANGE', { sessionId });
    } else {
      // all good, continue to release
      actionContext.dispatch('CONFLICT_CHECK_NO_CHANGE', { sessionId });
    }
  });
};

export const releaseGradebook = (actionContext: $TSFixMe, { sessionId, groupId, userGradebookReleases }: $TSFixMe) => {
  // batch update to release grades for groupId
  actionContext.dispatch('RELEASE_GRADEBOOK_START', { sessionId });

  releaseGradebookAPIUtil(groupId, userGradebookReleases)
    .then((result) => {
      actionContext.dispatch('RELEASE_GRADEBOOK_SUCCESS', { sessionId });
      actionContext.dispatch('GRADEBOOK_LOCKED', { sessionId, lockedFor: 1000 * 5 }); // TODO: tiered locking times
    })
    .catch((error) => {
      actionContext.dispatch('RELEASE_GRADEBOOK_FAIL', {
        sessionId,
        errorMessage: error.responseJSON.message,
      });

      Sentry.captureException(error, {
        extra: { type: 'Gradebook Release Failure' },
      });
    });
};

export const revertDraft = (actionContext: $TSFixMe, { sessionId, groupId, userGradebookReverts }: $TSFixMe) => {
  actionContext.dispatch('REVERT_DRAFT_GRADEBOOK_START', { sessionId });

  revertDraftGradebookAPIUtil(groupId, userGradebookReverts)
    .then((result) => {
      actionContext.dispatch('REVERT_DRAFT_GRADEBOOK_SUCCESS', { sessionId });
    })
    .catch((error) => {
      actionContext.dispatch('REVERT_DRAFT_GRADEBOOK_FAIL', {
        sessionId,
        errorMessage: error.responseJSON.message,
      });

      Sentry.captureException(error, {
        extra: { type: 'Gradebook Revert Draft Failure' },
      });
    });
};

export const setGradebookImportNotification = (actionContext: $TSFixMe, { sessionId, message }: $TSFixMe) => {
  actionContext.dispatch('SET_GRADEBOOK_IMPORT_NOTIFICATION', { sessionId, message });
};

export const receiveTableInstance = (actionContext: $TSFixMe, { instance }: $TSFixMe) => {
  actionContext.dispatch('RECEIVE_TABLE_INSTANCE', { instance });
};

export const updateComment = (actionContext: $TSFixMe, { sessionId, row, col, value }: $TSFixMe) => {
  actionContext.dispatch('UPDATE_COMMENT', { sessionId, row, col, value });
};

export const searchTable = (actionContext: $TSFixMe, { searchValue }: $TSFixMe) => {
  actionContext.dispatch('SEARCH_TABLE', { searchValue });
};

export const hideColumn = (actionContext: $TSFixMe, { sessionId, groupId, col, version }: $TSFixMe) => {
  const itemId = actionContext.getStore('GradebookStore').getItemIdByColumn(sessionId, col);

  actionContext.dispatch('HIDE_GRADES_FOR_ITEM', { sessionId, itemId, col });
};

export const unhideColumn = (actionContext: $TSFixMe, { sessionId, groupId, col, version }: $TSFixMe) => {
  const itemId = actionContext.getStore('GradebookStore').getItemIdByColumn(sessionId, col);

  actionContext.dispatch('UNHIDE_GRADES_FOR_ITEM', { sessionId, itemId, col });
};

export const updateCell = (actionContext: $TSFixMe, { sessionId, type, row, col, previousValue, value }: $TSFixMe) => {
  if (previousValue === value) {
    return;
  }
  actionContext.dispatch('UPDATE_CELL', { sessionId, type, row, col, previousValue, value });
};

export const removeOverride = (
  actionContext: $TSFixMe,
  { sessionId, type, row, col, previousValue, value }: $TSFixMe
) => {
  actionContext.dispatch('REMOVE_OVERRIDE', { sessionId, row, col });
};

export const ignoreCellUpdate = (actionContext: $TSFixMe, { sessionId, row, col, previousValue, value }: $TSFixMe) => {
  actionContext.dispatch('IGNORE_CELL_UPDATE', { sessionId, row, col, previousValue, value });
};

export const attachItemMeta = (actionContext: $TSFixMe, { sessionId, type, enablePrePenaltyGrades }: $TSFixMe) => {
  actionContext.dispatch('ATTACH_ITEM_META', { sessionId, type, enablePrePenaltyGrades });
};

export const attachFormulaValues = (actionContext: $TSFixMe, { sessionId, cells, recalculationType }: $TSFixMe) => {
  actionContext.dispatch('ATTACH_FORMULA_VALUES', { sessionId, cells, recalculationType });
};

export const fillDraggedCell = (
  actionContext: $TSFixMe,
  { sessionId, type, sourceCorners, targetCorners }: $TSFixMe
) => {
  actionContext.dispatch('FILL_DRAGGED_CELL', { sessionId, type, sourceCorners, targetCorners });
};

export const validateGradebook = (actionContext: $TSFixMe, { sessionId, isValid }: $TSFixMe) => {
  actionContext.dispatch('VALIDATE_GRADEBOOK', { sessionId, isValid });
};

export const setDisplayFormat = (actionContext: $TSFixMe, { displayFormat }: $TSFixMe) => {
  actionContext.dispatch('SET_DISPLAY_FORMAT', { displayFormat });
};

export const handleFilterDrafts = (actionContext: $TSFixMe, { shouldFilter }: $TSFixMe) => {
  actionContext.dispatch('SET_FILTER_DRAFTS', { shouldFilter });
};

export const handleFilterStaff = (actionContext: $TSFixMe, { showStaffLearners }: $TSFixMe) => {
  actionContext.dispatch('SET_FILTER_VIEW_AS_LEARNER_GRADES', { showStaffLearners });
};
